import { Lock, User as UserIcon, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { useState } from "react";
import type { User } from "@/types";
import { setToken, apiUrl } from "../../utils/api";
import { socketStore } from "../../stores/socket.store";
import { Logo } from "../design/Logo";
import { FundingButton } from "../modals/FundingModal";

const LoginScreen = ({ onLogin }: { onLogin: (u: User) => void }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        if (data.token) {
            setToken(data.token);
            socketStore.reauth();
        }
        if (data.user.force_reset) {
            const { userStore } = await import('../../stores/user.store');
            userStore.showForceResetModal = true;
            onLogin(data.user);
        } else {
            onLogin(data.user);
        }
      } else {
        setError(data.message || 'Anmeldung fehlgeschlagen. Prüf Benutzername und Passwort.');
      }
    } catch (err) {
      setError('Keine Verbindung zum Server.');
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "block w-full pl-10 pr-3 py-3 rounded-lg bg-black/30 border border-line text-fg placeholder-fg-muted/50 focus:outline-none focus:border-ember/70 focus:ring-2 focus:ring-ember/20 focus:bg-black/50 text-sm transition-all duration-150";

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-ink relative overflow-hidden px-4">
        {/* Quiet ambient: one soft ember glow + faint dot grid — restraint */}
        <div className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute -top-[30%] left-1/2 -translate-x-1/2 w-[70%] h-[60%] rounded-full bg-ember/10 blur-[130px]" />
            <div className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        </div>

      <div className="w-full max-w-sm z-10">
        <div className="flex flex-col items-center mb-7">
            <Logo size={40} withWordmark />
            <p className="text-fg-muted mt-3 text-sm">Deine Server. Deine Maschine. Deine Kontrolle.</p>
        </div>

        <div className="bg-surface-2 border border-line rounded-2xl shadow-2xl overflow-hidden">
            <div className="h-[2px] w-full gm-powerline" />
            <div className="p-6 sm:p-7">
                <h1 className="text-xl font-semibold text-fg tracking-tight">Willkommen zurück</h1>
                <p className="text-fg-muted text-sm mt-1 mb-6">Melde dich an, um deine Server zu verwalten.</p>

                <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="block text-[11px] uppercase tracking-wider text-fg-muted font-medium ml-0.5">Benutzername</label>
                        <div className="relative group">
                            <UserIcon size={17} className="absolute inset-y-0 left-3 my-auto text-fg-muted group-focus-within:text-ember transition-colors" />
                            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className={fieldCls} placeholder="Benutzername" required autoFocus autoComplete="username" />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="block text-[11px] uppercase tracking-wider text-fg-muted font-medium ml-0.5">Passwort</label>
                        <div className="relative group">
                            <Lock size={17} className="absolute inset-y-0 left-3 my-auto text-fg-muted group-focus-within:text-ember transition-colors" />
                            <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={fieldCls + " pr-10"} placeholder="••••••••" required />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-fg-muted hover:text-fg transition-colors" aria-label="Passwort anzeigen">
                                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/25 text-red-400 text-sm rounded-lg px-3 py-2.5 animate-in fade-in slide-in-from-top-1">
                            {error}
                        </div>
                    )}

                    <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-ink bg-ember hover:bg-ember-2 gm-ember-glow transition-all duration-150 active:scale-[0.99] disabled:opacity-60 disabled:cursor-wait focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember/50">
                        {loading ? <><Loader2 size={17} className="animate-spin" /> Anmelden…</> : <>Anmelden <ArrowRight size={16} /></>}
                    </button>
                </form>
            </div>
        </div>

        <div className="mt-6 flex justify-center">
            <FundingButton mode="login-hint" />
        </div>

        <p className="mt-4 text-center text-fg-muted/70 text-xs">
            GameManager · © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
